import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema } from '../../utils/seo';

export default function ListingHoutBayPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // SEO Configuration
  useSEO({
    title: 'Hout Bay Professional Painters - Coastal Painting Specialists | Residential & Commercial',
    description: 'Expert coastal painting services in Hout Bay, Llandudno, and Noordhoek. Specialized in weather-resistant coatings for beachfront properties. Over 15 years of coastal painting experience.',
    keywords: 'painters Hout Bay, coastal painting Hout Bay, house painters Hout Bay, residential painting, commercial painting, beachfront painting',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateLocalBusinessSchema({
          name: 'Painters Hout Bay',
          description: 'Coastal painting specialists in Hout Bay',
          address: 'Hout Bay, Cape Town',
          priceRange: '$$',
          serviceArea: ['Hout Bay', 'Llandudno', 'Noordhoek', 'Camps Bay', 'Kommetjie']
        }),
        generateServiceSchema({
          name: 'Coastal Painting Services',
          description: 'Specialized coastal and residential painting',
          provider: 'Painters Hout Bay',
          areaServed: ['Hout Bay', 'Llandudno', 'Noordhoek'],
          serviceType: ['Coastal Painting', 'Residential Painting', 'Commercial Painting']
        })
      ]
    }
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const listing = {
    name: "Hout Bay Coastal Painters",
    location: "Hout Bay, Cape Town",
    website: "https://paintershoutbay.co.za",
    heroImage: "https://public.readdy.ai/ai/img_res/d1090b01c0773aecea4f78df8f8ec6fe.jpg",
    gallery: [
      "https://readdy.ai/api/search-image?query=freshly%20painted%20coastal%20house%20exterior%20with%20white%20walls%20overlooking%20Hout%20Bay%20harbor%20with%20boats%20and%20mountains%20professional%20finish%20in%20bright%20sunlight&width=800&height=600&seq=houtbaygallery1&orientation=landscape",
      "https://readdy.ai/api/search-image?query=interior%20room%20painting%20in%20progress%20with%20professional%20painter%20applying%20coastal%20colors%20to%20walls%20using%20roller%20clean%20workspace%20with%20drop%20cloths%20and%20painting%20supplies&width=800&height=600&seq=houtbaygallery2&orientation=landscape",
      "https://readdy.ai/api/search-image?query=coastal%20property%20exterior%20painting%20project%20with%20scaffolding%20professional%20painters%20working%20on%20beachfront%20structure%20with%20ocean%20backdrop&width=800&height=600&seq=houtbaygallery3&orientation=landscape",
      "https://readdy.ai/api/search-image?query=beautifully%20painted%20coastal%20interior%20living%20room%20with%20ocean%20view%20windows%20elegant%20nautical%20color%20scheme%20modern%20furniture%20natural%20lighting%20professional%20finish&width=800&height=600&seq=houtbaygallery4&orientation=landscape"
    ],
    services: [
      {
        title: "Residential Painting",
        description: "Transform your Hout Bay home with our expert residential painting services. We specialize in coastal properties that require special attention to weather protection and salt damage prevention.",
        features: ["Interior walls & ceilings", "Exterior facades with marine coatings", "Trim & woodwork protection", "Color consultation for coastal homes"]
      },
      {
        title: "Coastal Protection",
        description: "Specialized coatings and treatments designed specifically for Hout Bay's coastal environment. Protect your property from salt spray, wind, and moisture damage.",
        features: ["Marine-grade paint systems", "Salt damage repair & prevention", "Wind-resistant coatings", "Moisture barrier treatments"]
      },
      {
        title: "Exterior Painting",
        description: "Expert exterior painting services that withstand Hout Bay's unique coastal conditions. We use premium weather-resistant materials for long-lasting results.",
        features: ["Weather-resistant coatings", "Facade restoration", "Deck & patio painting", "Roof coating & waterproofing"]
      }
    ],
    serviceAreas: ["Hout Bay", "Llandudno", "Imizamo Yethu", "Hangberg", "Tierboskloof", "Hout Bay Harbor"],
    about: "Hout Bay Coastal Painters is the leading painting company serving Hout Bay and the surrounding coastal areas. With over 14 years of specialized experience in coastal property painting, we have established ourselves as the most trusted painting contractor in this unique seaside community. Our team of skilled professionals understands the specific challenges that Hout Bay's coastal environment presents and delivers solutions that protect and beautify your property.",
    aboutExtended: [
      "We specialize in both residential and coastal protection painting projects, offering comprehensive solutions that address the unique challenges of painting in a marine environment. Our expertise extends across interior painting, exterior painting with marine-grade coatings, salt damage repair, waterproofing, and specialized protective finishes that withstand Hout Bay's coastal conditions.",
      "What sets us apart is our deep understanding of coastal painting requirements and our commitment to using only premium marine-grade paints and materials specifically designed for seaside properties. Our painters are fully trained in coastal painting techniques, certified, and insured, giving you complete peace of mind throughout your project.",
      "We understand that Hout Bay properties face unique challenges from salt spray, strong winds, and moisture. That's why we take the time to assess your property's specific needs and recommend the most appropriate protective coatings and painting systems. Whether you're looking to refresh a beachfront cottage or protect a hillside home, we deliver exceptional results that stand the test of time and weather."
    ],
    ourApproach: [
      "Comprehensive coastal property assessment",
      "Salt damage inspection and repair",
      "Premium marine-grade paints and coatings",
      "Specialized coastal application techniques",
      "Weather-resistant finishing systems",
      "Extended warranty for coastal conditions"
    ],
    whyChoose: [
      "14+ years coastal painting experience",
      "Marine-grade coating specialists",
      "Free coastal property assessment",
      "Weather-resistant materials",
      "Salt damage repair experts",
      "Satisfaction guaranteed"
    ],
    testimonials: [
      {
        name: "David Thompson",
        rating: 5,
        text: "Hout Bay Coastal Painters did an outstanding job on our beachfront property. They really understand the challenges of painting in this coastal environment. The finish has held up beautifully despite the harsh conditions!"
      },
      {
        name: "Sarah van Niekerk",
        rating: 5,
        text: "Professional service from start to finish. They repaired salt damage on our exterior walls and applied marine-grade coatings. The house looks amazing and we're confident it's well protected."
      },
      {
        name: "Michael Roberts",
        rating: 5,
        text: "Excellent work on our Hout Bay home. The team was knowledgeable about coastal painting requirements and used high-quality materials. Very pleased with the results and their attention to detail."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`sticky top-0 z-50 border-b border-gray-800 transition-all duration-300 ${isScrolled ? 'bg-black text-white' : 'bg-black text-white'}`}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="text-3xl font-bold whitespace-nowrap">
              <span className="text-white">PAINTE</span>
              <span className="text-[#e9f638]">RRR</span>
            </Link>
            <nav className="hidden md:flex items-center space-x-8">
              <Link to="/" className="hover:text-[#e9f638] transition whitespace-nowrap">Home</Link>
              <Link to="/about" className="hover:text-[#e9f638] transition whitespace-nowrap">About</Link>
              <Link to="/contact" className="bg-[#e9f638] text-black px-6 py-2 rounded-lg font-semibold hover:bg-white transition whitespace-nowrap cursor-pointer">Get a Quote</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative h-96 w-full overflow-hidden">
        <img 
          src={listing.heroImage}
          alt={listing.name}
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 container mx-auto px-4 pb-8">
          <h1 className="text-5xl font-bold text-white mb-2">{listing.name}</h1>
          <p className="text-xl text-gray-200 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            {listing.location}
          </p>
        </div>
      </div>

      {/* Contact Bar */}
      <div className="bg-[#e9f638] py-4">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <a 
              href={listing.website}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-black text-white px-8 py-3 rounded-lg font-bold hover:bg-gray-800 transition whitespace-nowrap cursor-pointer"
            >
              Visit Website
            </a>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-gray-50 border-b border-gray-200 sticky top-[73px] z-40">
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'overview' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('services')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'services' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Services
            </button>
            <button
              onClick={() => setActiveTab('gallery')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'gallery' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Gallery
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-semibold whitespace-nowrap border-b-2 transition cursor-pointer ${
                activeTab === 'reviews' ? 'border-[#e9f638] text-black' : 'border-transparent text-gray-500 hover:text-black'
              }`}
            >
              Reviews
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {activeTab === 'overview' && (
              <div>
                <section className="mb-12">
                  <h2 className="text-3xl font-bold mb-6">About Hout Bay Coastal Painters</h2>
                  <p className="text-gray-700 text-lg leading-relaxed mb-4">{listing.about}</p>
                  {listing.aboutExtended.map((paragraph, index) => (
                    <p key={index} className="text-gray-700 text-lg leading-relaxed mb-4">{paragraph}</p>
                  ))}
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Our Professional Approach</h3>
                  <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                    <ul className="space-y-3">
                      {listing.ourApproach.map((step, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-checkbox-circle-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                          <span className="text-gray-700 text-lg">{step}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </section>

                <section className="mb-12">
                  <h3 className="text-2xl font-bold mb-6">Why Choose Hout Bay Coastal Painters</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {listing.whyChoose.map((reason, index) => (
                      <div key={index} className="flex items-start">
                        <i className="ri-check-line text-[#e9f638] text-2xl mr-3 mt-1"></i>
                        <span className="text-gray-700 text-lg">{reason}</span>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h3 className="text-2xl font-bold mb-6">Areas We Serve</h3>
                  <p className="text-gray-700 text-lg mb-4">We proudly serve the following areas in and around Hout Bay:</p>
                  <div className="flex flex-wrap gap-3">
                    {listing.serviceAreas.map((area, index) => (
                      <span key={index} className="bg-gray-100 text-black px-4 py-2 rounded-full font-semibold">
                        {area}
                      </span>
                    ))}
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'services' && (
              <div>
                <h2 className="text-3xl font-bold mb-4">Our Professional Painting Services</h2>
                <p className="text-gray-700 text-lg mb-8">We offer a comprehensive range of coastal painting services tailored to meet the unique needs of Hout Bay properties.</p>
                <div className="space-y-8">
                  {listing.services.map((service, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200 hover:border-[#e9f638] transition">
                      <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                      <p className="text-gray-700 mb-4 text-lg">{service.description}</p>
                      <h4 className="font-semibold text-lg mb-3">What We Offer:</h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center text-gray-700">
                            <i className="ri-arrow-right-s-line text-[#e9f638] mr-2"></i>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'gallery' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Project Gallery</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {listing.gallery.map((image, index) => (
                    <div key={index} className="h-80 w-full overflow-hidden rounded-lg shadow-lg hover:shadow-2xl transition">
                      <img 
                        src={image}
                        alt={`Project ${index + 1}`}
                        className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-500 cursor-pointer"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === 'reviews' && (
              <div>
                <h2 className="text-3xl font-bold mb-8">Customer Reviews</h2>
                <div className="space-y-6">
                  {listing.testimonials.map((testimonial, index) => (
                    <div key={index} className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
                      <div className="flex items-center mb-4">
                        <div className="flex mr-4">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <i key={i} className="ri-star-fill text-[#e9f638] text-xl"></i>
                          ))}
                        </div>
                        <span className="font-bold text-lg">{testimonial.name}</span>
                      </div>
                      <p className="text-gray-700 text-lg leading-relaxed">{testimonial.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-lg p-6 sticky top-24">
              <h3 className="text-2xl font-bold mb-6">Contact Information</h3>
              
              <div className="space-y-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-map-pin-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Service Areas</p>
                    <p className="text-gray-600">Hout Bay, Llandudno, Imizamo Yethu</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-time-line text-2xl text-[#e9f638]"></i>
                  </div>
                  <div>
                    <p className="font-semibold">Business Hours</p>
                    <p className="text-gray-600">Mon-Fri: 8AM-5PM</p>
                    <p className="text-gray-600">Sat: 9AM-1PM</p>
                  </div>
                </div>
              </div>
              
              <a 
                href={listing.website}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#e9f638] text-black text-center py-3 rounded-lg font-bold hover:bg-black hover:text-[#e9f638] transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                Visit Website
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-black text-white border-t border-gray-800">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <h3 className="text-2xl font-bold mb-4">
                <span className="text-white">PAINTE</span>
                <span className="text-[#e9f638]">RRR</span>
              </h3>
              <p className="text-gray-400">South Africa's premier directory for professional house painters.</p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-[#e9f638] cursor-pointer">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Service Areas</h4>
              <ul className="space-y-2 text-gray-400">
                <li>Hout Bay</li>
                <li>Llandudno</li>
                <li>Imizamo Yethu</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-[#e9f638]">Connect</h4>
              <p className="text-gray-400">Find the perfect painter for your project today.</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-sm text-gray-400">
            <p>© 2025 PAINTERRR. All rights reserved.</p>
            <div className="flex space-x-4 mt-4 md:mt-0">
              <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="hover:text-[#e9f638] cursor-pointer">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
